import { Injectable, Inject } from '@angular/core';
import { Common } from '@icc/shared/helpers';
import { core } from '@icc/shared/helpers';
import { APP_CONFIG, AppConfigFactory } from '@icc/shared/config';
import { ConfigurationsService } from '@icc/configurator/shared';
import { RollerShutterActiveConfiguration } from '@icc/shutter/types';
import { EventBusService, ConfiguratorsDataService } from "@icc/shared";
import { WindowActiveConfiguration } from '@icc/window-door/types';
import { ColorsGroupsService } from '../colors-groups.service';

/**
 * Serwis do obliczania parametrów rolet (waga)
 *
 * Obsługuje:
 * - Wagę skrzynki
 * - Wagę profili rolety
 * - Wagę prowadnic
 * - Wagę napędu
 * - Wagę akcesoriów
 */
@Injectable()
export class RollerParametersService {
    constructor(
        private eventBusService: EventBusService,
        private configurationsService: ConfigurationsService,
        private configuratorsDataService: ConfiguratorsDataService,
        private colorsGroupsService: ColorsGroupsService,
        @Inject(APP_CONFIG) private config: AppConfigFactory
    ) {
        // Rejestruj eventy dla roller shutter
        this.eventBusService.subscribe(
            [
                'saveGuideProfile',
                'saveSlatProfile',
                'grayPolystyreneChanged',
                'changedDrive',
                'changedDriveManual',
                // Wspólne eventy:
                'setSystem',
                'changedStep',
                'icc-redraw',
            ],
            (data) => {
                // EventBus może przekazywać konfigurację w różnych miejscach
                const conf =
                    data.activeConfiguration
                    || this.configurationsService.conf.Current;

                if (RollerShutterActiveConfiguration.is(conf)) {
                    this.count(conf);
                }
            }
        );
    }

    /**
     * Główna metoda licząca parametry rolety
     */
    count(conf: RollerShutterActiveConfiguration): void {
        if (Common.isUndefined(conf) || !conf.RollerShutter) {
            return;
        }

        conf.Weight = this.countRollerShutterWeight(conf);
    }

    /**
     * Funkcja licząca wagę rolety
     */
    countRollerShutterWeight(conf: RollerShutterActiveConfiguration | WindowActiveConfiguration): number {
        const boxPrices = this.configuratorsDataService.data.rollerShutterBoxSizesPrices;
        const roller = conf.RollerShutter;

        if (
            !Common.isObject(roller.system)
            || !Common.isDefined(boxPrices)
            || !Common.isDefined(boxPrices[roller.system.id])
            || !Common.isDefined(roller.realBoxHeight)
            || !Common.isArray(roller.shutters)
        ) {
            return 0;
        }

        const boxWeight = this.countBoxWeight(roller, boxPrices[roller.system.id], conf.Width);
        const { profileWeight, driveWeight, guideWeight, reelWeight } = this.countShuttersWeight(roller);
        const accessoryWeight = this.countAccessoryWeight(conf, roller);

        return boxWeight + profileWeight + driveWeight + guideWeight + accessoryWeight + reelWeight;
    }

    /**
     * Funkcja licząca wagę skrzynki (dobór wg wysokości skrzynki i grupy kolorów)
     */
    private countBoxWeight(roller: any, systemPrices: any, width: number): number {
        let boxWeightMb = 0;
        let maxRollerHeight = 0;
        let boxHeightData: any = null;
        for (const minHeight in systemPrices) {
            if (
                systemPrices.hasOwnProperty(minHeight)
                && Number(minHeight) <= roller.realBoxHeight
                && Number(minHeight) >= maxRollerHeight
            ) {
                maxRollerHeight = Number(minHeight);
                boxHeightData = systemPrices[minHeight];
                boxWeightMb = systemPrices[minHeight].box_weight;
            }
        }

        if (boxHeightData && Common.isArray(boxHeightData.colorGroups) && boxHeightData.colorGroups.length) {
            boxWeightMb = this.countBoxWeightByColorGroup(roller, boxHeightData, boxWeightMb);
        }

        return (core.num(boxWeightMb) * width) / 1000;
    }

    /**
     * Funkcja dobierająca wagę skrzynki (za mb) wg grupy kolorów
     */
    private countBoxWeightByColorGroup(roller: any, boxHeightData: any, defaultBoxWeightMb: number): number {
        const windowColorGroups = this.configuratorsDataService.data.windowColorGroups || [];

        let colorGroupData;
        if (roller.system.box_color_type === 'bicolor') {
            const boxInnerGroups = windowColorGroups.filter(
                (el: any) =>
                    el.roller_systems?.indexOf(roller.system.id) > -1
                    && Common.isArray(el.roller_element)
                    && el.roller_element.indexOf('box_inner') > -1
            );
            const boxGuideOuterGroups = windowColorGroups.filter(
                (el: any) =>
                    el.roller_systems?.indexOf(roller.system.id) > -1
                    && Common.isArray(el.roller_element)
                    && el.roller_element.indexOf('box_guide_outer') > -1
            );
            const boxInnerColorGroup =
                this.colorsGroupsService.findGroupForColor(roller.colors.boxInner, boxInnerGroups) || [];
            const boxOuterColorGroup =
                this.colorsGroupsService.findGroupForColor(roller.colors.boxGuideOuter, boxGuideOuterGroups) || [];
            colorGroupData = boxHeightData.colorGroups.find(
                (el: any) =>
                    boxOuterColorGroup.some((cg: any) => +el.colorGroupId === +cg?.id)
                    && boxInnerColorGroup.some((cg: any) => +el.colorGroupInnerId === +cg?.id)
            );
        } else if (roller.system.box_color_type === 'bilateral') {
            const boxGroups = windowColorGroups.filter(
                (el: any) =>
                    el.roller_systems?.indexOf(roller.system.id) > -1
                    && Common.isArray(el.roller_element)
                    && el.roller_element.indexOf('box') > -1
                    && el.roller_element.indexOf('box_inner') === -1
                    && el.roller_element.indexOf('box_guide_outer') === -1
            );
            const boxColorGroup =
                this.colorsGroupsService.findGroupForColor(roller.colors.box, boxGroups) || [];
            colorGroupData = boxHeightData.colorGroups.find(
                (el: any) => boxColorGroup.some((cg: any) => +el.colorGroupId === +cg?.id)
            );
        }
        return colorGroupData ? colorGroupData.weight : defaultBoxWeightMb;
    }

    /**
     * Funkcja licząca wagę profili, prowadnic, napędów i rur nawojowych dla wszystkich rolek
     */
    private countShuttersWeight(roller: any): {
        profileWeight: number;
        driveWeight: number;
        guideWeight: number;
        reelWeight: number;
    } {
        let profileWeight = 0;
        let driveWeight = 0;
        let guideWeight = 0;
        let profileWeightForDrive = 0;
        let profileWidthForDrive = 0;
        let reelWeight = 0;

        roller.shutters.forEach((shutter: any, index: number) => {
            const shutterProfileWeight = this.getRollerWeight(roller, shutter);
            profileWeight += shutterProfileWeight;
            profileWeightForDrive += shutterProfileWeight;
            profileWidthForDrive += shutter.realWidth;

            const { leftGuideRailProfile, rightGuideRailProfile } = this.getGuideRailProfiles(roller, shutter);
            const { guideWeight: shutterGuideWeight, skipDrive } =
                this.countGuideWeight(roller, shutter, index, leftGuideRailProfile, rightGuideRailProfile);
            guideWeight += shutterGuideWeight;

            reelWeight += this.countReelWeight(roller, shutter);

            if (skipDrive) {
                return;
            }

            driveWeight += this.countDriveWeight(roller, profileWeightForDrive, profileWidthForDrive);
            profileWeightForDrive = 0;
            profileWidthForDrive = 0;
        });

        return { profileWeight, driveWeight, guideWeight, reelWeight };
    }

    /**
     * Funkcja pobierająca profile prowadnic (lewej i prawej) dla danej rolki
     */
    private getGuideRailProfiles(roller: any, shutter: any): { leftGuideRailProfile: any; rightGuideRailProfile: any } {
        const leftGuideRail = Common.isArray(roller.guideRails)
            ? roller.guideRails.find((g: any) => g.leftOf === shutter.id)
            : null;
        const rightGuideRail = Common.isArray(roller.guideRails)
            ? roller.guideRails.find((g: any) => g.rightOf === shutter.id)
            : null;
        const leftGuideRailProfile = leftGuideRail
            ? this.configuratorsDataService.data.rollerShutterProfiles.find(
                (el: any) => el.id === leftGuideRail.id
            )
            : null;
        const rightGuideRailProfile = rightGuideRail
            ? this.configuratorsDataService.data.rollerShutterProfiles.find(
                (el: any) => el.id === rightGuideRail.id
            )
            : null;
        return { leftGuideRailProfile, rightGuideRailProfile };
    }

    /**
     * Funkcja licząca wagę prowadnic dla danej rolki.
     *
     * Prowadnica współdzielona między dwiema sąsiednimi rolkami (shutter.commonRail === true dla
     * rolki po prawej stronie tej prowadnicy) jest liczona tylko raz - jako "prawa" prowadnica
     * rolki znajdującej się po jej lewej stronie. Rolka po prawej stronie takiej prowadnicy pomija
     * doliczanie swojej "lewej" prowadnicy, bo to ten sam fizyczny profil.
     *
     * Gdy prowadnica jest wspólna z następną rolką (skipDrive), napęd dla tej rolki jest dobierany
     * dopiero przy kolejnej rolce (po zsumowaniu szerokości/masy profili obu rolek).
     */
    private countGuideWeight(
        roller: any,
        shutter: any,
        index: number,
        leftGuideRailProfile: any,
        rightGuideRailProfile: any
    ): { guideWeight: number; skipDrive: boolean } {
        let guideWeight = 0;

        if (shutter.mosquito) {
            guideWeight +=
                ((core.num(leftGuideRailProfile?.mass) + core.num(rightGuideRailProfile?.mass))
                    * shutter.realHeight) / 1000;
        } else {
            if (!shutter.commonRail) {
                guideWeight += (core.num(leftGuideRailProfile?.mass) * shutter.realHeight) / 1000;
            }

            guideWeight += (core.num(rightGuideRailProfile?.mass) * shutter.realHeight) / 1000;
        }

        const nextShutter = roller.shutters[index + 1];
        const skipDrive = Common.isObject(nextShutter) && nextShutter.commonRail;

        return { guideWeight, skipDrive };
    }

    /**
     * Funkcja licząca wagę napędu dobranego wg zsumowanej szerokości/wagi profili rolki
     */
    private countDriveWeight(roller: any, profileWeightForDrive: number, profileWidthForDrive: number): number {
        if (!Common.isObject(roller.drive) || !Common.isArray(roller.drive.prices)) {
            return 0;
        }
        const drive = roller.drive.prices.find(
            (element: { from: number; to: number }) =>
                element.from <= profileWidthForDrive && element.to >= profileWeightForDrive
        );
        return Common.isObject(drive) ? core.num(drive.weight) : 0;
    }

    /**
     * Funkcja licząca wagę rury nawojowej dla danej rolki
     */
    private countReelWeight(roller: any, shutter: any): number {
        if (roller.roundReel && core.num(roller.roundReel.weight)) {
            return (core.num(roller.roundReel.weight) * shutter.realWidth) / 1000;
        }
        return 0;
    }

    /**
     * Funkcja licząca wagę akcesoriów (dodatki, elementy sterowania, szary styropian)
     */
    private countAccessoryWeight(
        conf: RollerShutterActiveConfiguration | WindowActiveConfiguration,
        roller: any
    ): number {
        let accessoryWeight = 0;

        if ((conf.type === 'roller_shutter' || conf.type === 'external_blind') && Common.isArray(conf.Accessories)) {
            accessoryWeight += this.sumWeight(conf.Accessories);
        }

        if (Common.isArray(roller.driveElements)) {
            accessoryWeight += this.sumWeight(roller.driveElements);
        }

        if (roller.grayPolystyrene && core.num(roller.grayPolystyrene.weight)) {
            const polystyreneWidth = roller.shutters.reduce(
                (prev: number, cur: any) => prev + cur.realWidth, 0
            );
            accessoryWeight += (core.num(roller.grayPolystyrene.weight) * polystyreneWidth) / 1000;
        }

        return accessoryWeight;
    }

    /**
     * Funkcja licząca wagę rolki
     */
    getRollerWeight(roller: any, shutter: any): number {
        let weight = 0;
        if (roller && Common.isObject(roller.profile)) {
            let width = shutter.realWidth;
            let height = shutter.realHeight;

            const slat = Common.isArray(roller.slats)
                ? roller.slats.find((g: any) => g.rollerId === shutter.id)
                : null;
            const slatProfile = slat
                ? this.configuratorsDataService.data.rollerShutterProfiles.find(
                    (el: any) => el.id === slat.id
                )
                : null;

            if (this.config().IccConfig.Configurators.roller_shutter.realDimensions) {
                const leftGuideRail = roller.guideRails.find((g: any) => g.leftOf === shutter.id);
                const rightGuideRail = roller.guideRails.find((g: any) => g.rightOf === shutter.id);

                if (leftGuideRail && rightGuideRail) {
                    const leftGuideRailProfile =
                        this.configuratorsDataService.data.rollerShutterProfiles.find(
                            (el: any) => el.id === leftGuideRail.id
                        );
                    const rightGuideRailProfile =
                        this.configuratorsDataService.data.rollerShutterProfiles.find(
                            (el: any) => el.id === rightGuideRail.id
                        );
                    if (leftGuideRailProfile && rightGuideRailProfile) {
                        width -=
                            leftGuideRailProfile.widthOut
                            - leftGuideRailProfile.depth
                            + rightGuideRailProfile.widthOut
                            - rightGuideRailProfile.depth;
                    }
                }

                if (slatProfile) {
                    height =
                        Math.ceil(
                            (height + roller.realBoxHeight / 2 - slatProfile.widthOut)
                                / roller.profile.width
                        )
                            * roller.profile.width
                        + slatProfile.widthOut;
                }
            }

            weight =
                ((Number(width) * Number(height)) / 1000000)
                * (parseFloat(roller.profile.weight_m2) || 0);

            if (slatProfile) {
                weight += (Number(width) / 1000) * (parseFloat(slatProfile.mass) || 0);
            }
        }

        return weight;
    }

    private sumWeight(arr: any[]): number {
        let weight = 0;
        for (let i = 0; i < arr.length; i++) {
            weight += core.num(arr[i].weight);
        }
        return weight;
    }
}
